"use client"

import { useEffect } from "react"

import { accessTokenStore } from "@/lib/api/token-store"
import { useNotificationsStore } from "@/lib/store/notifications"
import { usePwaStore } from "@/lib/store/pwa"

const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000 // 30 minutes
// Module-level so a `controllerchange` event firing in two close succession
// (rare race, but documented) doesn't trigger a second reload.
let isReloading = false

/**
 * Registers the PWA service worker (production only), drives the
 * `updateReady` state that the update banner consumes, and keeps the
 * notification feed in sync: hydrate on launch / focus / when the SW
 * signals a new push arrived.
 */
export function ServiceWorker() {
  useEffect(() => {
    const hydrate = () => {
      // Skip when signed out — /notifications is authenticated and a 401 here
      // would trip the global unauthorized handler into a hard redirect.
      if (!accessTokenStore.get()) return
      useNotificationsStore.getState().hydrateFromServer()
    }

    hydrate()

    const onVisible = () => {
      if (document.visibilityState === "visible") hydrate()
    }
    window.addEventListener("focus", hydrate)
    document.addEventListener("visibilitychange", onVisible)

    let onMessage: ((e: MessageEvent) => void) | undefined
    let onControllerChange: (() => void) | undefined
    let onUpdateFocus: (() => void) | undefined
    let updateIntervalId: ReturnType<typeof setInterval> | undefined

    if ("serviceWorker" in navigator) {
      onMessage = (e: MessageEvent) => {
        if (e.data?.type === "notifications:refresh") hydrate()
      }
      navigator.serviceWorker.addEventListener("message", onMessage)

      if (process.env.NODE_ENV === "production") {
        // `controllerchange` is the signal that a waiting SW has taken over,
        // which happens after the user clicks Reload on the update banner.
        // Listening early — before registration resolves — guarantees we don't
        // miss the event during the brief moment skipWaiting → activate runs.
        //
        // But the SAME event also fires on the first install, when the freshly
        // activated SW calls clients.claim() and takes control of this
        // previously-uncontrolled page. Reloading there is the launch-time
        // glitch (a flash mid-splash / mid-dashboard). A genuine update always
        // has a controller already, so gate on that: no prior controller =
        // first-install claim = don't reload.
        const hadController = !!navigator.serviceWorker.controller
        onControllerChange = () => {
          if (isReloading || !hadController) return
          isReloading = true
          window.location.reload()
        }
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          onControllerChange
        )

        navigator.serviceWorker
          .register("/sw.js", { scope: "/", updateViaCache: "none" })
          .then((reg) => {
            // If an update from a prior session is already waiting, surface
            // it immediately. Without this the banner would only appear after
            // the next updatefound, which might never come for this tab.
            if (reg.waiting && navigator.serviceWorker.controller) {
              usePwaStore.getState().setSwUpdateReady(true)
            }

            reg.addEventListener("updatefound", () => {
              const newWorker = reg.installing
              if (!newWorker) return
              newWorker.addEventListener("statechange", () => {
                // `controller` exists → this is an UPDATE, not first install.
                // First install gets a silent activate (no banner).
                if (
                  newWorker.state === "installed" &&
                  navigator.serviceWorker.controller
                ) {
                  usePwaStore.getState().setSwUpdateReady(true)
                }
              })
            })

            const tick = () => {
              reg.update().catch(() => {
                // network blip / quota — try again next tick
              })
            }
            onUpdateFocus = tick
            window.addEventListener("focus", tick)
            updateIntervalId = setInterval(tick, UPDATE_CHECK_INTERVAL_MS)
          })
          .catch(() => {
            // registration failures are non-fatal for the app
          })
      }
    }

    return () => {
      window.removeEventListener("focus", hydrate)
      document.removeEventListener("visibilitychange", onVisible)
      if (onMessage)
        navigator.serviceWorker.removeEventListener("message", onMessage)
      if (onControllerChange)
        navigator.serviceWorker.removeEventListener(
          "controllerchange",
          onControllerChange
        )
      if (onUpdateFocus) window.removeEventListener("focus", onUpdateFocus)
      if (updateIntervalId) clearInterval(updateIntervalId)
    }
  }, [])

  return null
}
